import React, { Component } from 'react';
import ModalModule from '../modal';
import UI from '../ui';
import Utils from '../utils';

const { Modal, ModalButtonActionType, ModalContext } = ModalModule;
const { WrappedInputTextareaWithLabel } = UI;
const { Logger, Validation, Communication } = Utils;

export default class AddCostCodesModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			costCodesText: '',
			validation: 0, // 0 = untouched, -1 = failed, 1 = valid
			errorMessage: '',
			clean: true,
		};
	}

	setCostCodesText = e => {
		this.setState({ costCodesText: e.target.value, clean: false });
	};

	// split input text at comma and trim whitespace
	parseCostCodes = () => {
		const { costCodesText } = this.state;

		return costCodesText
			.split(',')
			.map(code => code.trim())
			.filter(code => code !== '');
	};

	/**
	 * Return true if there are any duplicate cost codes in the array.
	 * @param {array} costCodes Array of new cost codes.
	 * @returns {boolean}
	 */
	checkForDuplicateCostCodes = (codes = []) => {
		const existedCodes = {};

		return codes.reduce((isDuplicate, code) => {
			const codeToken = code.toLowerCase();

			if (isDuplicate) {
				return isDuplicate;
			} else {
				const isExist = existedCodes[codeToken] || false;

				// mark the code as existed
				existedCodes[codeToken] = true;

				return isExist;
			}
		}, false);
	};

	validateInput = parsedArray => {
		const {
			data: { rowData, remainingCodes },
		} = this.props;

		// build a string of all cost codes in lowercase
		const allCostCodesStr = rowData
			.map(item => `[${item.Code}]`)
			.join(',')
			.toLowerCase();

		// result of validation
		const validateResult = { validation: 1, errorMessage: '' };

		// check if user input is empty
		if (parsedArray.length === 0) {
			validateResult.validation = -1;
			validateResult.errorMessage = 'Name is required.';
		}
		// check if more cost codes are being added than the remaining cost code limit
		else if (parsedArray.length > remainingCodes) {
			validateResult.validation = -1;
			validateResult.errorMessage =
				'Cost Code limit exceeded. Please upgrade your subscription by contacting Customer Service.';
		}
		// check if new cost codes contains duplicate names
		else if (this.checkForDuplicateCostCodes(parsedArray)) {
			validateResult.validation = -1;
			validateResult.errorMessage = 'Duplicate names cannot be added.';
		} else {
			parsedArray.forEach(code => {
				const codeToken = code.toLowerCase();

				if (validateResult.validation !== -1) {
					// check if code name already exists (case insensitive)
					if (allCostCodesStr.indexOf(`[${codeToken}]`) !== -1) {
						validateResult.validation = -1;
						validateResult.errorMessage = 'Duplicate names cannot be added.';
					}

					// check if each code is over 30 characters
					if (codeToken.length > 30) {
						validateResult.validation = -1;
						validateResult.errorMessage = 'Name cannot exceed 30 characters.';
					}
				}
			});
		}

		this.setState(validateResult);

		// return true if it passes all error checks
		return validateResult.validation === 1;
	};

	addCostCodes = async data => {
		const {
			modalId,
			data: { onSuccess, customerId },
		} = this.props;
		const { closeModal, showError } = this.context;

		const response = await Communication.addCostCodes(data, customerId);

		if (Communication.isSuccessful(response)) {
			onSuccess();
		} else {
			showError({ response });
		}

		closeModal(modalId);
	};

	// calls AddCostCodes AJAX call after parsing and validating
	onSaveButtonClick = () => {
		const { costCodesText, errorMessage } = this.state;
		const { showError } = this.context;
		const parsedText = this.parseCostCodes(costCodesText);

		Logger.saveActionData('Add Cost Codes Button Clicked');

		if (!this.validateInput(parsedText)) {
			return showError({
				data: {
					title: 'Incomplete Fields',
					description: Validation.buildValidationErrorDescription({
						Name: errorMessage,
					}),
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});
		}

		// disable "Save" button while adding cost codes in the backgrond
		this.setState(
			{
				clean: true,
			},
			() => {
				// make a call to add cost code(s)
				this.addCostCodes(parsedText);
			}
		);
	};

	onFieldBlur = () => {
		const { costCodesText } = this.state;
		const parsedText = this.parseCostCodes(costCodesText);

		this.validateInput(parsedText);
	};

	render() {
		const { modalId } = this.props;
		const { costCodesText, validation, errorMessage, clean } = this.state;

		const primaryButton = {
			type: ModalButtonActionType.Ok,
			text: 'Save',
			onClick: this.onSaveButtonClick,
			disabled: clean,
			dataqa: 'add-cost-codes-save-button',
		};

		return (
			<Modal
				modalId={modalId}
				className="common-cost-codes add-cost-codes-modal"
				header="Add Cost Code"
				primaryButton={primaryButton}
				focusOnShow={false}
				size="small"
			>
				<div className="body">
					<p>Add one or more cost codes separated by commas:</p>
					<WrappedInputTextareaWithLabel
						label="Name:"
						isInputInvalid={validation === -1}
						errorMessage={errorMessage}
						uiElementConfig={{
							rows: 2,
							cols: 43,
							value: costCodesText,
							onChange: this.setCostCodesText,
							onBlur: this.onFieldBlur,
							dataqa: 'addcostcodes',
							autoFocus: true,
						}}
					/>
				</div>
			</Modal>
		);
	}
}

AddCostCodesModal.contextType = ModalContext;
